import { Translation } from '@ps/web-shared-components';
import { format } from 'date-fns';
import { formatAddress } from 'localized-address-format';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import useWizardContext from '@swf/core/hooks/useWizardContext';
import { select } from '@swf/core/store';
import type { WizardContext } from '@swf/core/types/Wizard';
import { adaptDobAcrossTimezones } from '@swf/verification/IncodeProvider/utils';

import Address from '../../../../Address/Address';
import City from '../../../../City/City';
import Country from '../../../../Country/Country';
import InfoPrompt from '../../../../InfoPrompt/InfoPrompt';
import PostalCode from '../../../../PostalCode/PostalCode';
import TextInput from '../../../../TextInput/TextInput';
import State from '../../RMFAddress/components/Fields/components/StateField';
import GenderToggles from '../../RMFGender/components/GenderToggles/GenderToggles';
import GivenName from '../../RMFLegalName/components/GivenName/GivenName';
import MiddleName from '../../RMFLegalName/components/MiddleName/MiddleName';
import SummaryBox from '../SummaryBox/SummaryBox';

import {
  TXTCLI_Address,
  TXTCLI_Country_canXt_be_changed,
  TXTCLI_Date_of_Birth,
  TXTCLI_Full_Name,
  TXTCLI_Gender,
  TXTCLI_You_must_be_a_resident_of_the__ELL,
  TXTWCL_Last_name
} from './DataSummaryUSPA.messages.json';
import { itemBox, itemsWrapper } from './DataSummaryUSPA.scss';

export interface Props {
  handleCompleteForm: () => void;
}

const DataSummaryUSPA: React.FC<Props> = ({ handleCompleteForm }) => {
  const {
    values: { firstName, lastName, gender, country, streetAddress, city, zip, fiscalCode },
    error
  }: WizardContext = useWizardContext();
  const savedDob = useSelector(select.account.getDateOfBirth);

  const requiresNameEdit = !firstName || !lastName;
  const requiresAddressEdit = !country || !city || !zip;
  const requiresGenderEdit = !gender;

  const [editFullName, setEditFullName] = useState(requiresNameEdit);
  const [editAddress, setEditAddress] = useState(requiresAddressEdit);
  const [editGender, setEditGender] = useState(requiresGenderEdit);
  const [userDOB, setUserDOB] = useState('');

  const formattedStreetAddress = streetAddress;
  const formattedAddress = formatAddress({
    postalCountry: country,
    locality: city,
    postalCode: zip,
    addressLines: [formattedStreetAddress]
  }).join('\n');

  useEffect(() => {
    if (savedDob && userDOB === '') {
      const adaptedDOB = adaptDobAcrossTimezones(savedDob);
      const formattedDob = format(adaptedDOB, 'MM/dd/yyyy');
      setUserDOB(formattedDob);
    }
  }, [savedDob, userDOB]);

  const getInfoText = () => TXTCLI_You_must_be_a_resident_of_the__ELL;
  useEffect(() => {
    if (!requiresNameEdit) {
      setEditFullName(false);
    }

    if (!requiresAddressEdit) {
      setEditAddress(false);
    }
  }, [error, requiresAddressEdit, requiresNameEdit]);

  useEffect(() => {
    const allFieldsCompleted = (firstName || editFullName) && (formattedAddress || editAddress);
    if (allFieldsCompleted) {
      handleCompleteForm();
    }
  }, [
    editAddress,
    editFullName,
    firstName,
    fiscalCode,
    formattedAddress,
    handleCompleteForm,
    lastName
  ]);

  return (
    <div className={itemsWrapper}>
      <div className={itemBox}>
        {editFullName ? (
          <>
            <GivenName autoFocus={false} />
            <MiddleName />
            <TextInput label={TXTWCL_Last_name} id="lastName" disabled value={lastName} />
          </>
        ) : (
          <SummaryBox
            title={TXTCLI_Full_Name}
            details={`${firstName}  ${lastName}`}
            onClick={() => setEditFullName(true)}
          />
        )}
      </div>

      {
        <div className={itemBox}>
          {editGender ? (
            <GenderToggles />
          ) : (
            <SummaryBox
              title={TXTCLI_Gender}
              details={gender}
              onClick={() => setEditGender(true)}
            />
          )}
        </div>
      }
      {
        <div className={itemBox}>
          <SummaryBox editable={false} title={TXTCLI_Date_of_Birth} details={userDOB} />
        </div>
      }
      <div className={itemBox}>
        {editAddress ? (
          <>
            <Country
              country={country}
              fieldName="country"
              isRmfFlow
              labelContent={
                <label>
                  <InfoPrompt
                    title={{
                      translation: TXTCLI_Country_canXt_be_changed
                    }}
                  >
                    <p>
                      <Translation {...getInfoText()} />
                    </p>
                  </InfoPrompt>
                </label>
              }
            />
            <Address country={country} fieldName="streetAddress" value={formattedStreetAddress} />
            <City fieldName="city" />
            <PostalCode country={country} fieldName="zip" />
            <State country={country} fieldName="state" />
          </>
        ) : (
          <SummaryBox
            title={TXTCLI_Address}
            details={formattedAddress}
            onClick={() => setEditAddress(true)}
          />
        )}
      </div>
    </div>
  );
};

export default DataSummaryUSPA;
